import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { injectIntl } from 'react-intl';
import withStyles from 'isomorphic-style-loader/withStyles';
import { toast } from 'react-toastify';
import s from './AddGreeting.scss';
import GreetingTabs from './components/Tabs';
import Card from './components/Card';
import Grid from '../Grid';
import Clickable from '../Clickable';
import withResponsiveWrapper from '../../decorators/withResponsiveWrapper';
import Page from '../Page';
import {
  addGreetingMessages as messages,
  greetingCategoryMessages,
} from '../../defineMessages';
import { getGreeting as uGetGreeting } from '../../actions/greeting';
import {
  validateImage,
  getDataURLFromFile,
  getBase64ImageUrl,
} from './helpers/fileHelpers';

import imagePlaceholder from '../../assets/images/upload-image.svg';
import greetingImages from '../../assets/json/greetings.json';
import { stringContainsEmoji } from '../../helpers';

class AddGreeting extends React.Component {
  constructor(props) {
    super(props);
    const { base64Image, text } = props.greeting;
    this.state = {
      displayImageUri: base64Image
        ? `data:image/jpeg;base64,${base64Image}`
        : imagePlaceholder,
      selectedGalleryCategory: 'birthday',
      base64Image,
      text,
      errors: [],
    };
    this.setInitialGreeting = this.setInitialGreeting.bind(this);
    this.getGreetingImages = this.getGreetingImages.bind(this);
    this.getLocalizedErrors = this.getLocalizedErrors.bind(this);
    this.setGalleryCategory = this.setGalleryCategory.bind(this);
    this.setGalleryImage = this.setGalleryImage.bind(this);
    this.setGreetingText = this.setGreetingText.bind(this);
    this.setUserImage = this.setUserImage.bind(this);
    this.cancel = this.cancel.bind(this);
    this.save = this.save.bind(this);
    this.selectedImageRef = React.createRef();
    this.myCanvasRef = React.createRef();
  }

  async componentDidMount() {
    await this.setInitialGreeting();
  }

  async setInitialGreeting() {
    const { getGreeting, greetingToken } = this.props;
    if (greetingToken) {
      const {
        data: { base64Image, greetingText },
        errors,
      } = await getGreeting(greetingToken);
      if (base64Image) {
        this.setState({
          displayImageUri: `data:image/jpeg;base64,${base64Image}`,
          base64Image,
        });
      }
      if (errors && errors.length) {
        this.setState({ errors: ['Could not load greeting.'] });
      }
      this.setGreetingText(greetingText);
    }
  }

  getGreetingImages = () => {
    const {
      intl: { locale, formatMessage },
    } = this.props;
    let counter = 0;
    const images = greetingImages[locale] || greetingImages.en;
    return Object.keys(images).map(c => {
      const label = formatMessage({ ...greetingCategoryMessages[c] });
      return {
        value: c,
        label,
        images: Object.keys(images[c]).map(i => ({
          imageUri: `${greetingImages.base}${
            greetingImages[locale] ? locale : 'en'
          }/${c}/${images[c][i]}`,
          // eslint-disable-next-line no-plusplus
          id: ++counter,
        })),
      };
    });
  };

  getLocalizedErrors = () => {
    const {
      intl: { formatMessage },
    } = this.props;
    const { errors } = this.state;
    return errors.map(error => {
      let msg = error;
      try {
        msg = formatMessage({ ...messages[error] });
      } catch (err) {
        console.error(`Error getting translated message for key: ${error}`);
      }
      return msg;
    });
  };

  setGalleryCategory = selectedGalleryCategory => {
    this.setState({
      selectedGalleryCategory,
    });
  };

  setGalleryImage = selectedGalleryImageId => {
    const { selectedGalleryCategory } = this.state;
    const gallery = this.getGreetingImages().find(
      ({ value }) => value === selectedGalleryCategory,
    );

    const img = gallery.images.find(
      ({ id }) => `${id}` === selectedGalleryImageId,
    );
    this.setState({
      selectedGalleryImageId,
      base64Image: null,
      selectedGalleryImageUrl: img && img.imageUri,
      displayImageUri: img && img.imageUri,
    });
  };

  setGreetingText = value => {
    this.setState({
      text: value,
    });
  };

  setUserImage = async files => {
    const { errors } = validateImage(files[0]);
    if (errors.length) {
      this.setState({
        errors,
        base64Image: null,
        selectedGalleryImageUrl: null,
        selectedGalleryImageId: null,
        displayImageUri: imagePlaceholder,
      });
      return;
    }
    try {
      const { url, data } = await getDataURLFromFile(files[0]);
      this.setState({
        errors: [],
        displayImageUri: url,
        base64Image: data,
        selectedGalleryImageUrl: null,
        selectedGalleryImageId: null,
      });
    } catch (err) {
      this.setState({
        errors: ['uploadError'],
        base64Image: null,
        selectedGalleryImageUrl: null,
        selectedGalleryImageId: null,
        displayImageUri: imagePlaceholder,
      });
    }
  };

  cancel = () => {
    const { onCancel } = this.props;
    if (onCancel) {
      onCancel();
    }
  };

  save = async () => {
    const { greetingToken, onUpdate, onAdd } = this.props;
    const { text, videoId, selectedGalleryImageUrl } = this.state;

    let { base64Image } = this.state;

    if (selectedGalleryImageUrl) {
      try {
        base64Image = getBase64ImageUrl(
          this.myCanvasRef.current,
          this.selectedImageRef.current,
        );
      } catch (err) {
        this.setState({ errors: ['fileReadError'] });
        return;
      }
    }
    if (!greetingToken && onAdd) {
      onAdd({
        text,
        base64Image,
        videoId,
      });
    } else if (greetingToken && onUpdate) {
      onUpdate({
        greetingToken,
        text,
        base64Image,
        videoId,
      });
    }
  };

  render() {
    const {
      intl: { formatMessage },
      loading,
      ...props
    } = this.props;

    const {
      errors,
      selectedGalleryImageId,
      selectedGalleryCategory,
      text,
      displayImageUri,
    } = this.state;

    const cards = this.getGreetingImages();

    if (errors.length > 0) {
      toast.error(this.getLocalizedErrors().join('<br />'));
    }

    const textContainsEmoji = stringContainsEmoji(text);

    return (
      <Page loading={loading} className={s.root}>
        <Grid container spacing={32}>
          <Grid item xs={24} md={18}>
            <GreetingTabs
              {...props}
              cards={cards}
              formatMessage={formatMessage}
              messages={messages}
              onUploadImage={this.setUserImage}
              onGalleryImageChange={this.setGalleryImage}
              onCategoryChange={this.setGalleryCategory}
              cardOccasionSelected={selectedGalleryCategory}
              cardSelected={selectedGalleryImageId}
            />
          </Grid>
          <Grid item xs={24} md={6}>
            <Card
              formatMessage={formatMessage}
              messages={messages}
              greetingText={text}
              greetingImage={displayImageUri}
              setGreetingText={this.setGreetingText}
              hasError={textContainsEmoji}
              onUploadImage={this.setUserImage}
            />
            <div>
              <canvas
                ref={this.myCanvasRef}
                className={s['add-greeting-canvas']}
              />
              <img
                src={displayImageUri}
                className={s['shadow-image']}
                ref={this.selectedImageRef}
                alt="base64-shadow"
                crossOrigin="anonymous"
              />
            </div>
          </Grid>
        </Grid>
        <Grid container spacing={16} justify="flex-end">
          <Grid item xs={24} sm="auto">
            <Clickable
              fluid
              basic
              label={formatMessage({
                ...messages.cancel,
              })}
              onClick={this.cancel}
            />
          </Grid>
          <Grid item xs={24} sm="auto">
            <Clickable
              disabled={textContainsEmoji}
              fluid
              primary
              label={formatMessage({
                ...messages.saveAndContinue,
              })}
              onClick={this.save}
              type="submit"
            />
          </Grid>
        </Grid>
      </Page>
    );
  }
}

AddGreeting.propTypes = {
  intl: PropTypes.shape().isRequired,
  greetingToken: PropTypes.string,
  basketId: PropTypes.string,
  getGreeting: PropTypes.func.isRequired,
  onAdd: PropTypes.func,
  onUpdate: PropTypes.func,
  onRemove: PropTypes.func,
  onCancel: PropTypes.func,
  greeting: PropTypes.shape(),
  loading: PropTypes.bool.isRequired,
};

AddGreeting.defaultProps = {
  greeting: {},
  greetingToken: null,
  basketId: null,
  onAdd: null,
  onRemove: null,
  onUpdate: null,
  onCancel: null,
};

const mapState = state => ({
  basketId: state.basket.id,
  loading: !!(state.greeting && state.greeting.greetingLoading),
  errors: (state.greeting && state.greeting.errors) || [],
});

// Prefixed with u to avoid linting errors (declared in upper scope)
const mapDispatch = {
  getGreeting: uGetGreeting,
};

export default compose(
  withStyles(s),
  connect(mapState, mapDispatch),
  withResponsiveWrapper(768),
  injectIntl,
)(AddGreeting);
